import type { AutonomousPort, AutonomousSummary } from '../consumer/autonomous';
import { createMonitoringRequest, MonitoringReadError } from './http';

function record(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new MonitoringReadError('WIRE');
  return value as Record<string, unknown>;
}
const id = (value: unknown) => typeof value === 'string' && /^[a-z][a-z0-9-]{0,63}$/.test(value);
const count = (value: unknown) => Number.isSafeInteger(value) && Number(value) >= 0;
const member = (value: unknown, allowed: string[]) =>
  typeof value === 'string' && allowed.includes(value);
const time = (value: unknown) =>
  value === null ||
  (typeof value === 'string' && /Z$/.test(value) && Number.isFinite(Date.parse(value)));
function requireWire(condition: boolean): void {
  if (!condition) throw new MonitoringReadError('WIRE');
}
export function parseAutonomousSummary(value: unknown, areaId: string): AutonomousSummary {
  const data = record(value),
    runner = record(data.runner),
    coverage = record(data.coverage);
  requireWire(
    data.instanceId === 'forest-demo-1' &&
      data.areaId === (areaId || null) &&
      data.scope === 'CURRENT_AUTHORIZED_INSTANCE' &&
      data.mode === 'SYNTHETIC_EXERCISE' &&
      member(data.registryBackend, ['sqlite', 'mysql']) &&
      data.asOf !== null &&
      time(data.asOf) &&
      count(data.configRevision) &&
      member(data.dataStatus, ['READY', 'STALE', 'NO_DATA', 'UNAVAILABLE']) &&
      member(runner.status, ['RUNNING', 'STOPPED', 'UNAVAILABLE']) &&
      ['lastHeartbeatAt', 'lastAcceptedAt', 'lastProcessedAt'].every((key) => time(runner[key])) &&
      (runner.error === null || typeof runner.error === 'string') &&
      [
        'configuredAreas',
        'enabledSources',
        'eligibleSources',
        'healthySources',
        'unknownSources',
        'unlocatedSources',
      ].every((key) => count(coverage[key])) &&
      ['observedWarningCount', 'predictiveAttentionCount', 'pendingEvaluationCount'].every(
        (key) => data[key] === null || count(data[key]),
      ) &&
      Array.isArray(data.sources),
  );
  const sources = data.sources as unknown[];
  const seen = new Set<string>();
  for (const item of sources) {
    const source = record(item);
    requireWire(
      id(source.sourceId) &&
        id(source.areaId) &&
        (!areaId || source.areaId === areaId) &&
        count(source.epoch) &&
        (Number(source.epoch) >= 1 || source.health === 'DISABLED') &&
        member(source.health, ['ACTIVE_HEALTHY', 'STALE', 'NO_DATA', 'DISABLED', 'UNAVAILABLE']) &&
        source.locationStatus === 'UNLOCATED' &&
        source.dataOrigin === 'SYNTHETIC_EXERCISE' &&
        time(source.latestObservedAt) &&
        time(source.latestReceivedAt) &&
        !seen.has(String(source.sourceId)) &&
        !('latitude' in source) &&
        !('longitude' in source),
    );
    seen.add(String(source.sourceId));
  }
  requireWire(
    Number(coverage.healthySources) <= Number(coverage.enabledSources) &&
      Number(coverage.eligibleSources) <= Number(coverage.enabledSources) &&
      Number(coverage.unknownSources) <= Number(coverage.enabledSources),
  );
  return value as AutonomousSummary;
}
export function createAutonomousAdapter(options: {
  readonly baseOrigin: string;
  readonly fetchImpl?: typeof fetch;
}): AutonomousPort {
  const request = createMonitoringRequest(options);
  return {
    read: async (areaId, signal) => {
      if (areaId && !id(areaId)) throw new MonitoringReadError('WIRE');
      const result = await request(
        `/read-models/normal-monitoring/autonomous${areaId ? `?areaId=${encodeURIComponent(areaId)}` : ''}`,
        (value) => parseAutonomousSummary(value, areaId),
        signal,
      );
      if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
      return result;
    },
  };
}
